import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Table, Loader, Select } from '@mantine/core';
import { getCurrencyName, formatCurrAmount } from '../../utils/script';
import { ICurrency, ICurrencyValuesForm } from '../../../libraries/currencies';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import CurrencyInput from '../../components/currency-input';
import { IconX } from '@tabler/icons-react';

interface CurrencyTableProps {
  selectedCurrencies: {
    gid: number;
    eid?: number;
    entid?: number;
    cid: number;
    minAmount: number;
    maxAmount: number;
    decAmount?: number;
  }[];
  currencyList: ICurrency[];
  loading: boolean;
  editable?: boolean;
  filteredCid?: ICurrency[];
  onAddCurrency?: (currency: ICurrencyValuesForm) => void;
  onRemoveCurrency?: (cid: number) => void;
  entity: { entid: number | 0; gid?: number | null };
  form: any;
  declarationAmount?: number;
  isNewLocation?: boolean;
  onCurrencyChange?: (cid: number, field: 'minAmount' | 'maxAmount' | 'decAmount', centsValue: number) => void;
  maxCurrencies: number;
}

const CurrencyTable = ({
  selectedCurrencies,
  currencyList,
  loading,
  editable,
  filteredCid,
  form,
  declarationAmount,
  isNewLocation,
  onAddCurrency,
  onRemoveCurrency,
  onCurrencyChange,
  maxCurrencies
}: CurrencyTableProps) => {
  const lang = useSelector((state: AppState) => state.lang.lang);
  const loginInfo = useSelector((state: AppState) => state.login.user);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const { eid } = useParams<string>();

  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    column: 'minAmount' | 'maxAmount' | 'decAmount';
    cid: number;
  } | null>(null);

  const updateCurrencyValue = (cid: number, field: 'minAmount' | 'maxAmount' | 'decAmount', centsValue: number) => {
    onCurrencyChange?.(cid, field, centsValue);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!tableWrapperRef.current) return;

      if (!tableWrapperRef.current.contains(event.target as Node)) {
        setEditingCell(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCurrencySelect = (currencyCid: string | null) => {
    if (!currencyCid) return;
    if (selectedCurrencies.length >= maxCurrencies) return;
    const selectedCurrency = filteredCid?.find(c => c.cid.toString() === currencyCid);
    if (!selectedCurrency) return;

    const newCurr: ICurrencyValuesForm = {
      gid: loginInfo.gid ?? 0,
      eid: eid ? Number(eid) : undefined,
      entid: loginInfo.entid,
      cid: selectedCurrency.cid,
      minAmount: 0,
      maxAmount: 0,
      decAmount: 0
    };

    const updated = [...selectedCurrencies, newCurr];
    form.setFieldValue('currencies', updated);
    onAddCurrency?.(newCurr);
  };

  const availableCurrencies = filteredCid?.filter(c => !selectedCurrencies.some(sc => sc.cid === c.cid)) || [];

  return (
    <>
      {editable && availableCurrencies.length > 0 && (
        <Select
          label={lang.GLOBAL_CURRENCY}
          placeholder={lang.EQUIPMENT_CURRENCY_SELECT}
          data={availableCurrencies.map(c => ({
            value: c.cid.toString(),
            label: getCurrencyName(c.cid)
          }))}
          onChange={handleCurrencySelect}
          value={''}
          disabled={selectedCurrencies.length >= maxCurrencies}
        />
      )}

      <div className="currency-table" ref={tableWrapperRef}>
        <Table.ScrollContainer minWidth={100}>
          <Table striped highlightOnHover verticalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
                <Table.Th>{lang.EQUIPMENT_STATE_LABEL_MIN_AMOUNT}</Table.Th>
                {(declarationAmount || isNewLocation) && <Table.Th>{lang.EQUIPMENT_STATE_LABEL_DEC_AMOUNT}</Table.Th>}
                <Table.Th>{lang.EQUIPMENT_STATE_LABEL_MAX_AMOUNT}</Table.Th>
                {editable && <Table.Th />}
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Loader color="#016273" />
                  </Table.Td>
                </Table.Tr>
              ) : selectedCurrencies.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5} style={{ textAlign: 'center' }}>
                    {lang.GLOBAL_NO_RESULTS}
                  </Table.Td>
                </Table.Tr>
              ) : (
                selectedCurrencies.map((currency, rowIndex) => {
                  const matchingCurrency = currencyList?.find(c => c.cid === currency.cid);

                  return (
                    <Table.Tr key={currency.cid}>
                      <Table.Td>{getCurrencyName(currency.cid)}</Table.Td>

                      {/* MIN */}
                      <Table.Td
                        onClick={() =>
                          editable &&
                          setEditingCell({
                            rowIndex,
                            column: 'minAmount',
                            cid: currency.cid
                          })
                        }
                      >
                        {editingCell?.rowIndex === rowIndex && editingCell.column === 'minAmount' ? (
                          <CurrencyInput
                            value={currency.minAmount}
                            currencySymbol={matchingCurrency?.symbol}
                            onChange={cents => updateCurrencyValue(currency.cid, 'minAmount', cents)}
                          />
                        ) : (
                          formatCurrAmount(currency.minAmount, matchingCurrency)
                        )}
                      </Table.Td>

                      {/* DEC */}
                      {(declarationAmount || isNewLocation) && (
                        <Table.Td
                          onClick={() =>
                            editable &&
                            setEditingCell({
                              rowIndex,
                              column: 'decAmount',
                              cid: currency.cid
                            })
                          }
                        >
                          {editingCell?.rowIndex === rowIndex && editingCell.column === 'decAmount' ? (
                            <CurrencyInput
                              value={currency.decAmount ?? 0}
                              currencySymbol={matchingCurrency?.symbol}
                              onChange={cents => updateCurrencyValue(currency.cid, 'decAmount', cents)}
                            />
                          ) : (
                            formatCurrAmount(currency.decAmount ?? 0, matchingCurrency)
                          )}
                        </Table.Td>
                      )}

                      {/* MAX */}
                      <Table.Td
                        onClick={() =>
                          editable &&
                          setEditingCell({
                            rowIndex,
                            column: 'maxAmount',
                            cid: currency.cid
                          })
                        }
                      >
                        {editingCell?.rowIndex === rowIndex && editingCell.column === 'maxAmount' ? (
                          <CurrencyInput
                            value={currency.maxAmount}
                            currencySymbol={matchingCurrency?.symbol}
                            onChange={cents => updateCurrencyValue(currency.cid, 'maxAmount', cents)}
                          />
                        ) : (
                          formatCurrAmount(currency.maxAmount, matchingCurrency)
                        )}
                      </Table.Td>

                      {editable && (
                        <Table.Td>
                          <IconX stroke={2} onClick={() => onRemoveCurrency?.(currency.cid)} />
                        </Table.Td>
                      )}
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>
    </>
  );
};

export default CurrencyTable;
